/** 行级差异结果 */
export interface DiffLine {
  type: 'equal' | 'added' | 'removed';
  leftLineNumber?: number;
  rightLineNumber?: number;
  content: string;
  wordDiffs?: WordDiff[];
}

/** 词级差异片段 */
export interface WordDiff {
  type: 'equal' | 'added' | 'removed';
  text: string;
}

/**
 * 合并相邻同类型片段，减少 DOM 节点数
 */
function mergeSegments(segments: WordDiff[]): WordDiff[] {
  if (segments.length === 0) return [];
  const merged: WordDiff[] = [{ ...segments[0] }];
  for (let i = 1; i < segments.length; i++) {
    const last = merged[merged.length - 1];
    if (last.type === segments[i].type) {
      last.text += segments[i].text;
    } else {
      merged.push({ ...segments[i] });
    }
  }
  return merged;
}

/**
 * 逐字符 LCS diff
 * 输入两个字符串，输出字符级差异片段（已合并相邻同类型）
 */
export function diffChars(left: string, right: string): WordDiff[] {
  const m = left.length;
  const n = right.length;

  // 构建 LCS DP 表
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (left[i - 1] === right[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // 回溯
  const segments: WordDiff[] = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && left[i - 1] === right[j - 1]) {
      segments.unshift({ type: 'equal', text: left[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      segments.unshift({ type: 'added', text: right[j - 1] });
      j--;
    } else {
      segments.unshift({ type: 'removed', text: left[i - 1] });
      i--;
    }
  }

  return mergeSegments(segments);
}

/**
 * 行级 LCS diff
 * 输入两个完整文本，输出行级差异结果
 * - 保留所有空白字符（不 trim、不 normalize）
 * - 相邻 removed+added 行自动配对并计算词级差异
 */
export function diffLines(left: string, right: string): DiffLine[] {
  // 空字符串处理：split('\n') 返回 ['']，单独处理避免不直观结果
  if (left === '' && right === '') return [];

  // 保留空行：split 不丢弃尾随空行
  const leftLines = left.split('\n');
  const rightLines = right.split('\n');

  // 单侧空文本：直接标记全部行
  if (left === '') {
    return rightLines.map((content, i) => ({
      type: 'added' as const,
      rightLineNumber: i + 1,
      content,
    }));
  }
  if (right === '') {
    return leftLines.map((content, i) => ({
      type: 'removed' as const,
      leftLineNumber: i + 1,
      content,
    }));
  }

  const m = leftLines.length;
  const n = rightLines.length;

  // 构建 LCS DP 表
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (leftLines[i - 1] === rightLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // 回溯生成 diff
  const raw: DiffLine[] = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && leftLines[i - 1] === rightLines[j - 1]) {
      raw.unshift({
        type: 'equal',
        leftLineNumber: i,
        rightLineNumber: j,
        content: leftLines[i - 1],
      });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      raw.unshift({
        type: 'added',
        rightLineNumber: j,
        content: rightLines[j - 1],
      });
      j--;
    } else {
      raw.unshift({
        type: 'removed',
        leftLineNumber: i,
        content: leftLines[i - 1],
      });
      i--;
    }
  }

  // 配对相邻 removed+added 行并计算词级差异
  const result: DiffLine[] = [];
  let idx = 0;
  while (idx < raw.length) {
    const line = raw[idx];
    if (
      line.type === 'removed' &&
      idx + 1 < raw.length &&
      raw[idx + 1].type === 'added'
    ) {
      const charDiffs = diffChars(line.content, raw[idx + 1].content);
      result.push({ ...line, wordDiffs: charDiffs });
      result.push({ ...raw[idx + 1], wordDiffs: charDiffs });
      idx += 2;
    } else {
      result.push(line);
      idx++;
    }
  }

  return result;
}
